// API endpoints for interacting with the backend services

// Base URL for API calls - update this based on your deployment
const API_BASE_URL = 'https://mksdca7616.execute-api.us-east-1.amazonaws.com';
const USER_API_BASE_URL = 'https://hsp1rr5g7l.execute-api.us-east-1.amazonaws.com';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'An error occurred');
  }
  return response.json();
};

export const adaptiveApi = {
  fetchFreeplayPuzzles: async ({ count, minRating, maxRating }) => {
    const response = await fetch(`${API_BASE_URL}/freeplay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ count, minRating, maxRating }),
    });

    const data = await handleResponse(response);
    const puzzles = Array.isArray(data) ? data : data?.puzzles;
    return puzzles ?? []
  },
};

// Lichess Puzzle API calls
export const lichessPuzzleApi = {
  // Initialize a new user and create their puzzle set
  // initializeUser: async (token, provider, email, rating, quantity) => {
  //   const response = await fetch(`${API_BASE_URL}/lichess-puzzle`, {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
  //     body: JSON.stringify({
  //       username: email,
  //       user_rating: rating,
  //       puzzle_quantity: quantity
  //     })
  //   });
  //   return handleResponse(response);
  // },

  // Fetch puzzles for a user
  fetchPuzzles: async (token, provider, email, rating, quantity, { setId, setName, startPuzzleIndex, initialTimeSpent, initialCycleNumber, sessionMode = 'full' } = {}) => {
    const response = await fetch(`${API_BASE_URL}/lichess-puzzle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
      body: JSON.stringify({
        set_index: setId,
        setName: setName,
        username: email,
        user_rating: rating,
        puzzle_quantity: quantity,
        isContinuing: startPuzzleIndex > 0 || initialTimeSpent > 0,
        puzzleIndex: startPuzzleIndex,
        currentTime: initialTimeSpent,
        cycleNumber: initialCycleNumber,
        sessionMode,
      })
    });
    if (response.status === 403) {
      const err = new Error('Forbidden');
      err.status = 403;
      throw err;
    }
    return handleResponse(response);
  },

  // // Create a new cycle
  // createNewCycle: async (token, provider, email, cycleNumber) => {
  //   const response = await fetch(`${API_BASE_URL}/lichess-puzzle`, {
  //     method: 'PUT',
  //     headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
  //     body: JSON.stringify({ 
  //       username: email,
  //       cycleNumber 
  //     })
  //   });
  //   return handleResponse(response);
  // },

  // Pause progress by updating current cycle
  pauseProgress: async (token, provider, email, { setId, currentTime, solvedCorrectly, puzzlesCompleted, cycleDone = false, midCycle, cycleNumber, wrongPuzzleIds, currentPuzzleAttempted, currentPuzzleFailed }) => {
    const response = await fetch(`${API_BASE_URL}/lichess-puzzle`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
      body: JSON.stringify({
        set_index: setId,
        username: email,
        currentTime,
        solvedCorrectly,
        puzzlesCompleted,
        cycleDone, 
        midCycle,
        cycleNumber,
        wrongPuzzleIds,
        currentPuzzleAttempted,
        currentPuzzleFailed,
      })
    });
    console.log(response.body);
    return handleResponse(response);
  },

  // Get user data
  getUserData: async (token, provider, email) => {
    const response = await fetch(`${API_BASE_URL}/lichess-puzzle?username=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
    });
    return handleResponse(response);
  },

  deleteTrainingSet: async (token, provider, userEmail, setId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/deleteTrainingSet?username=${encodeURIComponent(userEmail)}&setId=${setId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
      });

      if (!response.ok) {
        throw new Error('Failed to delete training set');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting training set:', error);
      throw error;
    }
  },
};

// User API calls (for subscription management)
export const userApi = {
  // Check user subscription status
  checkSubscription: async (token, provider, email) => {
    const response = await fetch(`${USER_API_BASE_URL}/${email}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
    });
    return handleResponse(response);
  },

  // Initialize a new user
  initializeUser: async (token, provider, email, name) => { // 99% sure this isn't being used but keeping it for now just in case
    const response = await fetch(`${API_BASE_URL}/user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
      body: JSON.stringify({ email, name })
    });
    return handleResponse(response);
  },

  // Decrement free month count
  decrementFreeMonth: async (token, provider, email) => { // 99% sure this isn't being used but keeping it for now just in case
    const response = await fetch(`${API_BASE_URL}/user?email=${encodeURIComponent(email)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
    });
    return handleResponse(response);
  }
};

// Stripe API calls
export const stripeApi = {
  // Create a checkout session
  // createCheckoutSession: async (email) => {
  //   const response = await fetch(`${API_BASE_URL}/stripe/create-checkout-session`, {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify({ email })
  //   });
  //   return handleResponse(response);
  // },

  // Cancel subscription
  cancelSubscription: async (token, provider, email) => {
    const response = await fetch(`https://o5s6iz75t2.execute-api.us-east-1.amazonaws.com/cancel-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
      body: JSON.stringify({ email })
    });
    return handleResponse(response);
  },

  // Create a payment intent
  createPaymentIntent: async (token, provider, plan, email, userName) => {
    const response = await fetch(
      'https://o5s6iz75t2.execute-api.us-east-1.amazonaws.com/create-payment',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
        body: JSON.stringify({ email, planType: plan, userName }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    return response.json();
  }
};

export const customPuzzleApi = {
  fetchPuzzles: async (token, provider, username, dateParam, isPaid) => {
    const body = {
      idToken: token,
      isPaid,
    }
    return await fetch(
      `https://dwlgrbp17c.execute-api.us-east-1.amazonaws.com/${username}${dateParam}`,
      { method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
        body: JSON.stringify(body),
      }
    );
  },

  checkSubscriptionStatus: async (token, provider, email) => {
    return await fetch(`https://hsp1rr5g7l.execute-api.us-east-1.amazonaws.com/${email}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
    });
  },

  cancelSubscription: async (token, provider, email) => {
    return await fetch('https://o5s6iz75t2.execute-api.us-east-1.amazonaws.com/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Authorization-Provider': provider,
      },
      body: JSON.stringify({
        email: email
      }),
    });
  },

  createPayment: async(token, provider, email, planType) => {
    return await fetch(
      'https://o5s6iz75t2.execute-api.us-east-1.amazonaws.com/create-payment',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': provider },
        body: JSON.stringify({ email, planType }),
      }
    );
  }
}

// Export a default object with all API functions
const api = {
  lichessPuzzle: lichessPuzzleApi,
  adaptive: adaptiveApi,
  user: userApi,
  stripe: stripeApi
};

export default api;
